import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  PieChart as PieChartIcon,
  GridOn as HeatmapIcon,
  DonutLarge as DonutIcon
} from '@mui/icons-material';

const CHART_TEMPLATES = [
  {
    type: 'bar',
    name: 'Bar Chart',
    description: 'Compare values across categories. Perfect for sales by region, performance metrics, or survey results.',
    icon: <BarChartIcon sx={{ fontSize: 48 }} />,
    defaultData: [
      { label: 'Q1', value: 45 },
      { label: 'Q2', value: 38 },
      { label: 'Q3', value: 52 },
      { label: 'Q4', value: 41 }
    ],
    variables: ['quarters', 'revenue', 'targets']
  },
  {
    type: 'line',
    name: 'Line Chart',
    description: 'Show trends over time. Ideal for growth metrics, stock prices, or temperature changes.',
    icon: <LineChartIcon sx={{ fontSize: 48 }} />,
    defaultData: [
      { x: 0, value: 10 },
      { x: 1, value: 25 },
      { x: 2, value: 15 },
      { x: 3, value: 30 },
      { x: 4, value: 28 }
    ],
    variables: ['timeline', 'growth_rate', 'projections']
  },
  {
    type: 'pie',
    name: 'Pie Chart',
    description: 'Display proportions of a whole. Great for market share, budget allocation, or demographic breakdown.',
    icon: <PieChartIcon sx={{ fontSize: 48 }} />,
    defaultData: [
      { label: 'Product A', value: 30 },
      { label: 'Product B', value: 25 },
      { label: 'Product C', value: 20 },
      { label: 'Product D', value: 15 },
      { label: 'Other', value: 10 }
    ],
    variables: ['categories', 'percentages', 'segments']
  },
  {
    type: 'donut',
    name: 'Donut Chart',
    description: 'Like pie charts but with a center for key metrics. Perfect for completion rates or portfolio distribution.',
    icon: <DonutIcon sx={{ fontSize: 48 }} />,
    defaultData: [
      { label: 'Completed', value: 75 },
      { label: 'In Progress', value: 20 },
      { label: 'Not Started', value: 5 }
    ],
    variables: ['status', 'completion_rate', 'tasks']
  },
  {
    type: 'heatmap',
    name: 'Heatmap',
    description: 'Visualize data density or intensity. Excellent for activity patterns, correlation matrices, or geographic data.',
    icon: <HeatmapIcon sx={{ fontSize: 48 }} />,
    defaultData: [
      { x: 'Mon', y: 'Morning', value: 30 },
      { x: 'Mon', y: 'Afternoon', value: 50 },
      { x: 'Mon', y: 'Evening', value: 20 },
      { x: 'Tue', y: 'Morning', value: 45 },
      { x: 'Tue', y: 'Afternoon', value: 60 },
      { x: 'Tue', y: 'Evening', value: 35 }
    ],
    variables: ['days', 'time_periods', 'activity_levels']
  }
];

// eslint-disable-next-line max-lines-per-function
const ChartInsertModal = ({ open, onClose, onInsert }) => {
  const [_selectedChart, setSelectedChart] = useState(null);

  const handleInsertChart = (chartTemplate) => {
    const chartConfig = {
      chartType: chartTemplate.type,
      chartData: chartTemplate.defaultData,
      chartOptions: {
        width: '100%',
        height: 400,
        title: `${chartTemplate.name} - {{chart_title}}`,
        xLabel: '{{x_axis_label}}',
        yLabel: '{{y_axis_label}}',
        colors: ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'],
        showLegend: true,
        showTooltip: true
      }
    };

    onInsert(chartConfig);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Insert D3.js Chart</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Select a chart type to visualize your data. Charts support template variables and will be populated with real data when generating reports.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {CHART_TEMPLATES.map((chart) => (
            <Grid
              key={chart.type}
              size={{
                xs: 12,
                sm: 6,
                md: 4
              }}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: 2,
                  borderColor: 'transparent',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  }
                }}
                onClick={() => setSelectedChart(chart)}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {chart.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {chart.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {chart.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                    {chart.variables.map((variable) => (
                      <Typography
                        key={variable}
                        variant="caption"
                        sx={{
                          backgroundColor: 'grey.100',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontFamily: 'monospace'
                        }}
                      >
                        {`{{${variable}}}`}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleInsertChart(chart)}
                  >
                    Add {chart.name}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            💡 How Chart Variables Work
          </Typography>
          <Typography variant="body2">
            Charts use template variables like <code>{`{{revenue}}`}</code> or <code>{`{{quarters}}`}</code> that will be replaced with actual data from your entity records when generating reports. The AI will automatically map your data to create beautiful visualizations.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChartInsertModal;